// Custom metal business cards. Targets: metal business cards 6,600, metal
// business card 1,000, custom metal credit card 1,900, custom metal business
// cards 590, custom metal cards 480, engraved business cards 140, metal gift
// cards 110, VIP cards 40. Every metal card job in the inbox closed. No
// working payment cards (no chips or magstripes).
const {
  quoted,
  setup,
  each,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'metalBusinessCards',
  slug: 'metal-business-cards',
  path: '/services/laser-engraving/metal-business-cards/',
  noun: 'card',
  crumb: 'Metal business cards',
  title: 'Custom Metal Business Cards, Laser Engraved | PrintAWorld',
  description:
    'Custom metal business cards and VIP cards laser engraved in NYC: black, stainless, brass and color metal, with cutouts, names and QR codes.',
  eyebrow: 'Metal business cards / engraved in New York',
  heading: 'Custom metal business cards, laser engraved in New York.',
  answer: `PrintAWorld laser-engraves custom metal business cards in New York: black anodized, stainless steel, brass and color metal cards with your logo, each person’s name and a QR code, plus cutouts through the metal. We also make metal VIP, membership and gift cards with a number on each. Engraving is a ${setup} setup plus ${each(small, 'card')}; the metal blanks are ${quoted.toLowerCase()}.`,
  cta: 'Get a card quote',
  heroImage: 'blackCard',
  ideaImages: ['stainlessCutout', 'brassVip', 'cardFan'],
  images: {
    blackCard: {
      file: 'black-card',
      alt: 'Matte black metal business card with a logo and name engraved in bright silver',
      caption: 'Black metal, engraved silver',
    },
    stainlessCutout: {
      file: 'stainless-cutout',
      alt: 'Brushed stainless business card with a logo cut through the metal and engraved details',
      caption: 'Stainless with a cut-through logo',
    },
    brassVip: {
      file: 'brass-vip',
      alt: 'Brushed brass VIP membership card engraved with a club name and member number in a black sleeve',
      caption: 'A numbered brass VIP card',
    },
    cardFan: {
      file: 'card-fan',
      alt: 'Fan of black metal business cards with the same design and different names',
      caption: 'One design, every name',
    },
  },
  intro: {
    eyebrow: 'Cards people keep',
    title: 'Metal business cards with your logo cut and engraved.',
    paragraphs: [
      'A metal card is heavy, it rings when it hits the table and nobody throws it away. We engrave the logo and details into the metal and can cut shapes and windows right through it.',
      'Order a set for the whole team with a name and title on each, or numbered VIP and membership cards for your members.',
    ],
  },
  ideas: {
    eyebrow: 'Metal card ideas',
    title: 'Black, stainless and brass metal cards.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Metal business cards, VIP cards and membership cards.',
    items: [
      {
        label: 'Teams',
        title: 'Business cards for everyone',
        body: 'One design, a name, title and contact on each, from a spreadsheet.',
      },
      {
        label: 'Founders and agents',
        title: 'A card that gets kept',
        body: 'For the meetings where the first impression matters: real estate, finance, design.',
      },
      {
        label: 'Clubs and venues',
        title: 'VIP and membership cards',
        body: 'Numbered metal cards for members, residents and regulars.',
      },
      {
        label: 'Brands',
        title: 'Metal gift and launch cards',
        body: 'A metal card in a PR mailer or a gift box, with a code or a message.',
      },
      {
        label: 'Events',
        title: 'Invitations and passes',
        body: 'A metal invitation or backstage pass, engraved with each guest’s name.',
      },
    ],
  },
  materials: {
    link: 'Black, stainless or brass',
    eyebrow: 'Metals',
    title: 'Black anodized, stainless steel and brass cards.',
    items: [
      {
        title: 'Black anodized aluminum',
        body: 'The classic black metal card. The engraving shows bright silver.',
      },
      {
        title: 'Stainless steel',
        body: 'Brushed or polished, heavier, with a dark engraved mark from our MOPA laser.',
      },
      {
        title: 'Brass and gold tones',
        body: 'Warm brass for VIP and membership cards.',
      },
      {
        title: 'Color and cutouts',
        body: 'Color anodized cards, UV-printed color logos and laser-cut windows through the card.',
      },
    ],
  },
  process: {
    title: 'From your design to a box of metal cards.',
    note: 'Standard lead time is 2–3 weeks after you approve the proof.',
    steps: [
      {
        title: 'Send the design and names',
        body: 'Your card design or logo, the metal, and a spreadsheet of names and details.',
      },
      {
        title: 'Approve a proof',
        body: 'We send a proof of each side, then engrave a sample card.',
      },
      {
        title: 'We engrave the run',
        body: 'Each card is engraved and checked against your list.',
      },
      {
        title: 'Packed per person',
        body: 'Cards are packed by name, shipped or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Metal business card pricing, per card.',
    cta: 'Price my cards',
    rows: [
      { label: 'Engraving, per card', value: small },
      { label: 'Metal card blanks', value: quoted },
      { label: 'Cutouts through the card', value: quoted },
      { label: 'Color by UV printing', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Cards for the whole team',
    title: 'One design, a card for every person.',
    paragraphs: [
      'Send the names in a spreadsheet. We engrave each card and pack them by person, so new hires can be added to the next order in the same design.',
      'Numbered VIP cards are engraved in sequence from your list.',
    ],
    cta: 'Plan a team order',
  },
  faqTitle: 'Metal business card questions, answered.',
  faqs: [
    {
      question: 'How much do metal business cards cost?',
      answer: costAnswer(
        small,
        `The metal card blanks, cutouts and color printing are ${quoted.toLowerCase()}. Larger orders cost less per card.`
      ),
    },
    {
      question: 'Can each card have a different name?',
      answer:
        'Yes. Send the names, titles and contact details in a spreadsheet; the design stays the same.',
    },
    {
      question: 'Can you add color to a metal card?',
      answer:
        'Engraving shows the metal itself. For a color logo we UV print it on the card, or start from a color anodized blank.',
    },
    {
      question: 'Can you put a QR code on a metal card?',
      answer:
        'Yes. QR codes engrave cleanly on anodized aluminum and stainless. We scan the sample before the run.',
    },
    {
      question: 'Do you make metal credit cards?',
      answer:
        'We make metal VIP, membership and gift cards, but not working payment cards with a chip or magnetic stripe.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('card'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer('Tell us if you need them for an event or launch.'),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your metal cards.',
    body: 'Send the design or logo, the metal, how many and the names. We’ll reply with a proof plan and a written quote.',
    checklist: [
      'Your card design or logo, vector if you have it',
      'Black, stainless, brass or color metal',
      'How many, and a names list',
      'Your in-hand date and delivery ZIP',
    ],
  },
  moreCards: ['metalAssetTags', 'corporateGifts', 'plaques'],
  schema: {
    name: 'Custom Metal Business Cards',
    alternateName: [
      'Metal business cards',
      'Engraved business cards',
      'Metal VIP cards',
      'Metal membership cards',
    ],
    serviceType: 'Laser-engraved metal cards',
  },
})
